import { useMutation } from '@apollo/client';
import {
  CREATE_BILLING_CHECKOUT_SESSION,
  type CreateBillingCheckoutSessionData,
  type CreateBillingCheckoutSessionVars,
} from '@queries/billing';
import { redirectToUrl } from '@services/billing-redirect';

const BILLING_CHECKOUT_SUCCESS_PATH = '/billing?checkout=success';
const BILLING_CHECKOUT_CANCEL_PATH = '/billing?checkout=cancel';

function getBillingCheckoutCallbackUrls(): { successUrl: string; cancelUrl: string } {
  return {
    successUrl: new URL(BILLING_CHECKOUT_SUCCESS_PATH, window.location.origin).toString(),
    cancelUrl: new URL(BILLING_CHECKOUT_CANCEL_PATH, window.location.origin).toString(),
  };
}

export interface StartBillingCheckoutOptions {
  priceId?: string;
  onMissingPriceId?: () => void;
  onMissingRedirectUrl?: () => void;
  onError?: (error: unknown) => void;
}

export function useBillingCheckout(): {
  isLoading: boolean;
  startBillingCheckout: (options?: StartBillingCheckoutOptions) => Promise<boolean>;
} {
  const [startCheckout, checkoutState] = useMutation<
    CreateBillingCheckoutSessionData,
    CreateBillingCheckoutSessionVars
  >(CREATE_BILLING_CHECKOUT_SESSION);

  const startBillingCheckout = async (options: StartBillingCheckoutOptions = {}): Promise<boolean> => {
    const defaultPriceId = import.meta.env.BILLING_STRIPE_PRICE_ID || import.meta.env.STRIPE_PRICE_ID || '';
    const { successUrl, cancelUrl } = getBillingCheckoutCallbackUrls();

    const priceId = options.priceId ?? defaultPriceId;

    if (!priceId) {
      options.onMissingPriceId?.();
      return false;
    }

    try {
      const result = await startCheckout({
        variables: {
          priceId,
          successUrl,
          cancelUrl,
        },
      });

      const redirectUrl = result?.data?.createBillingCheckoutSession?.url;
      if (!redirectUrl) {
        options.onMissingRedirectUrl?.();
        return false;
      }

      redirectToUrl(redirectUrl);
      return true;
    } catch (error) {
      options.onError?.(error);
      return false;
    }
  };

  return {
    isLoading: checkoutState.loading,
    startBillingCheckout,
  };
}
